import { useQuery } from '@tanstack/react-query';

import { BenefitStoreItem } from '../../types/benefit';
import { StoreItemResponse, StoreItemsResponse } from '../../types';
import { storeKeys } from '../../query-key-factory';
import { createQueryOptions } from '../../../query';
import { useStoreApi } from '../../api';

type GetCampaignsBenefitsRequest = {
  meta?: {
    campaignId?: string;
    causeId?: string;
    queryString?: string;
  };
};

const makeCauseQueryString = (causeId?: string) => {
  return `cause_id=${causeId}`;
};

const useGetCampaignBenefits = () => {
  const StoreApi = useStoreApi();
  return async (context: GetCampaignsBenefitsRequest) => {
    const { campaignId, queryString } = context.meta || {};
    const { data } = await StoreApi.get<StoreItemsResponse<BenefitStoreItem[]>>(
      `/store_items/${campaignId}/benefits?${queryString}`
    );
    return data;
  };
};

const useListCampaignBenefits = ({
  causeId,
  campaignId,
  queryOptions,
}: {
  causeId?: string;
  campaignId?: string;
  //@ts-expect-error - Benefits
  queryOptions?: CampaignSearch;
}) => {
  const causeQuery = makeCauseQueryString(causeId);

  const getCampaignBenefits = useGetCampaignBenefits();
  //@ts-expect-error - Benefits
  const { queryString, queryObject } = createQueryOptions<CampaignSearch>({
    search: queryOptions,
    existingSearchString: causeQuery,
  });

  const key =
    causeId && campaignId ? storeKeys.id(causeId).items.campaigns.id(campaignId).benefits.all : [];

  return useQuery({
    queryKey: [...key, queryObject],
    queryFn: getCampaignBenefits,
    enabled: !!campaignId && !!causeId,
    meta: { queryString, campaignId, causeId },
  });
};

const useShowCampaignBenefit = ({
  causeId,
  campaignId,
  benefitId,
}: {
  causeId?: string;
  campaignId?: string;
  benefitId?: string;
}) => {
  const StoreApi = useStoreApi();
  const causeQuery = makeCauseQueryString(causeId);

  const getCampaignBenefit = async () => {
    const { data } = await StoreApi.get<StoreItemResponse<BenefitStoreItem>>(
      `/store_items/${campaignId}/benefits/${benefitId}?${causeQuery}`
    );
    return data.store_item;
  };

  const key =
    campaignId && causeId && benefitId
      ? storeKeys.id(causeId).items.campaigns.id(campaignId).benefits.id(benefitId).detail
      : [];

  return useQuery({
    queryKey: key,
    queryFn: getCampaignBenefit,
    enabled: !!campaignId && !!causeId && !!benefitId,
  });
};

export { useListCampaignBenefits, useShowCampaignBenefit };
